import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { getDatabase, ref, get } from "firebase/database";
import "./styles/Orders.css";
import WebsiteFooter from "../components/WebsiteFooter";

const Orders = ({ loggedInUser }) => {
    const navigate = useNavigate();
    const database = getDatabase();

    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [productImages, setProductImages] = useState({}); // Store images for products

    // Fetch user orders on component mount
    useEffect(() => {
        const fetchOrders = async () => {
            if (loggedInUser) {
                try {
                    const userRef = ref(database, `Customers/${loggedInUser}/`);
                    const snapshot = await get(userRef);

                    if (snapshot.exists()) {
                        const userDetails = snapshot.val();
                        const ordersData = userDetails.Orders || {};
                        const ordersArray = Object.keys(ordersData).map((invoiceNo) => ({
                            invoiceNo,
                            ...ordersData[invoiceNo],
                        }));
                        setOrders(ordersArray);
                    } else {
                        console.log("No user data found");
                    }
                } catch (error) {
                    console.error("Error fetching user details:", error);
                } finally {
                    setLoading(false);
                }
            } else {
                const timestamp = new Date()
                window.gtag("event", 'login', {'timestamp': timestamp.toLocaleString() })
                navigate("/Login");
            }
        };

        fetchOrders();
    }, [database, loggedInUser, navigate]);

    const fetchProductImage = async (productName) => {
        if (productImages[productName]) return productImages[productName]; // Return cached image if available

        try {
            const productsRef = ref(database, "Products");
            const snapshot = await get(productsRef);

            if (snapshot.exists()) {
                const products = snapshot.val();
                const product = Object.values(products).find((item) => item.Name === productName);

                if (product?.Img) {
                    setProductImages((prev) => ({ ...prev, [productName]: product.Img }));
                    return product.Img;
                }
            }
        } catch (error) {
            console.error("Error fetching product image:", error);
        }
        return "default-image-url.jpg"; // Fallback for missing images
    };
    // const generateTrackingLink = (orderId) => {
    //     // You can replace this with actual channelId if available
    //     const channelId = selectedOrder.OrderDetails.channel_id; // replace with actual channel ID if needed
    //     return `https://www.shiprocket.in/track/${channelId}/${orderId}`;
    // };


    const handleOrderClick = (order) => {
        setSelectedOrder(order);
    };

    const handleItemClick = (itemName) => {
        const timestamp = new Date().toISOString();
        window.gtag("event", `${itemName}_viewed`, { 'timestamp': timestamp.toLocaleString() })
        navigate(`/Product/${itemName}`);
    };

    if (loading) {
        return <p>Loading...</p>;
    }

    if (!loggedInUser) {
        return null;
    }

    return (
        <>
            <div className="orders-section">
                <h2>Orders</h2>
                {!selectedOrder ? (
                    orders.length > 0 ? (
                        orders.map((order) => {
                            // Safely access nested properties
                            const orderId = order.OrderConfirmationDetails?.order_id || "N/A";
                            const orderDate = order.OrderDetails?.order_date
                                ? new Date(order.OrderDetails.order_date).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                })
                                : "N/A";
                            // const etd = order.OrderDetails?.estimatedETD || "N/A";
                            const orderTotal = (order.OrderDetails?.order_items || []).reduce(
                                (total, item) => total + item.selling_price * item.units,
                                0
                            ) + (order.OrderDetails?.shipping_charges || 0);

                            return (
                                <div
                                    key={order.invoiceNo}
                                    className="order-card"
                                    onClick={() => handleOrderClick(order)}
                                >
                                    <h3>Order Id: {orderId}</h3>
                                    <p>Date: {orderDate}</p>
                                    {/* <p>Delivered by:  {etd} </p> */}
                                    <div className="order-total item-row">
                                        <span>Total:</span>
                                        <span>₹{orderTotal}</span>
                                    </div>
                                </div>
                            );
                        })
                    ) : (
                        <p>No orders found</p>
                    )
                ) : (
                    <div className="order-details">
                        <h3>Order Id: {selectedOrder.OrderConfirmationDetails?.order_id}</h3>
                        <p>
                            Date:{" "}
                            {selectedOrder.OrderDetails?.order_date
                                ? new Date(selectedOrder.OrderDetails.order_date).toLocaleDateString("en-US", {
                                    month: "short",
                                    day: "numeric",
                                    year: "numeric",
                                })
                                : "N/A"}<br />
                            {/* Delivered by:   {selectedOrder.OrderDetails.estimatedETD} */}
                        </p>
                        <div className="product-cards">
                            {selectedOrder.OrderDetails?.order_items ? (
                                selectedOrder.OrderDetails.order_items.map((item, index) => (
                                    <div
                                        key={index}
                                        className="order-item"
                                        onClick={() => handleItemClick(item.name)}
                                    >
                                        <strong>{item.name}</strong>
                                        <div className="order-detail">
                                            <div className="order-left">
                                                <div className="item-row">
                                                    <span>Price:</span>
                                                    <span>₹{item.selling_price}</span>
                                                </div>
                                                <div className="item-row">
                                                    <span>Quantity:</span>
                                                    <span>{item.units}</span>
                                                </div>
                                            </div>
                                            <div className="order-right">
                                                <img
                                                    src={productImages[item.name] || fetchProductImage(item.name)}
                                                    alt={item.name}
                                                    className="order-image"
                                                />
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <p>No items found in the order.</p>
                            )}
                        </div>
                        <div className="order-summary">
                            <div className="shipping-charges item-row">
                                <span>Shipping Charges:</span>
                                <span>₹{selectedOrder.OrderDetails?.shipping_charges || 0}</span>
                            </div>
                            <div className="order-total item-row">
                                <span>Total:</span>
                                <span>
                                    ₹
                                    {(selectedOrder.OrderDetails?.order_items || []).reduce(
                                        (total, item) => total + item.selling_price * item.units,
                                        0
                                    ) + (selectedOrder.OrderDetails?.shipping_charges || 0)}
                                </span>
                            </div>
                        </div>
                        {/* <div className="tracking-section">
                            <h2>Track Your Order</h2>
                            {selectedOrder.ShipmentDetails?.shipment_id ? (
                                <iframe
                                    src={`https://widget.shiprocket.co/v1/track?shipment_id=${selectedOrder.ShipmentDetails.shipment_id}`}
                                    width="100%"
                                    height="250px"
                                    style={{ border: "none" }}
                                    title="Order Tracking"
                                ></iframe>
                            ) : (
                                <p>Tracking information is not available for this order.</p>
                            )}
                        </div> */}
                    </div>
                )}
            </div>
            <WebsiteFooter />
        </>
    );
};

export default Orders;
